// /apps/patient/src/pages/prescriptions/PrescriptionDetail.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { 
  Pill, Calendar, Building2, User, FileText,
  AlertCircle, Clock, Phone, MapPin, Hash, 
  Package, RefreshCw, AlertTriangle, Printer
} from 'lucide-react';
import { format } from 'date-fns';

interface PrescriptionDetail {
  id: string;
  created_at: number;
  medication_prescribed?: {
    drug_description: string;
    drug_name?: string;
    ndc?: string;
    sig?: {
      sig_text: string;
      sig_free_text?: string;
    };
    quantity?: {
      value: number;
      unit: string;
    };
    written_date?: {
      date?: string;
    };
    days_supply?: number;
    number_of_refills?: number;
    refills_remaining?: number;
    substitutions?: number;
    notes?: string;
  };
  pharmacy?: {
    ncpdp_id?: string;
    business_name: string;
    address?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      zip: string;
    };
    communication_numbers?: {
      primary_phone: string;
      fax?: string;
    };
  };
  prescriber?: {
    name: {
      first_name?: string;
      last_name?: string;
    };
    communication_numbers?: {
      primary_phone?: {
        number: string;
      };
    }
  };
  prescribed_date?: string;
  sent_date?: string;
  expiration_date?: string;
  last_filled_date?: string;
  diagnosis_codes?: string[];
  controlled_substance_schedule?: string;
}

export function PrescriptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState<PrescriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchPrescriptionDetail();
    }
  }, [id]);

  const fetchPrescriptionDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientApiService.getPrescriptionDetail(id!);
      setPrescription(data);
    } catch (err: any) {
      console.error('Error fetching prescription detail:', err);
      setError(err.message || 'Failed to load prescription details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Prescription Details" showBack />
        <div className="mt-8">
          <div className="animate-pulse">
            <div className="h-64 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !prescription) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="Prescription Details" showBack />
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700">{error || 'Prescription not found'}</p>
          <button
            onClick={() => navigate('/prescriptions')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Back to Prescriptions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader 
        title="Prescription Details" 
        showBack
        actions={
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        }
      />

      {/* Medication Header */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Pill className="h-8 w-8 text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {prescription.medication_prescribed?.drug_description || 'Unknown Medication'}
            </h2>
            {prescription.medication_prescribed?.drug_name && (
              <p className="text-sm text-gray-600 mt-1">
                Generic: {prescription.medication_prescribed.drug_name}
              </p>
            )}
            
            {prescription.controlled_substance_schedule && (
              <div className="mt-3">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium border border-red-200 bg-red-50 text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  Schedule {prescription.controlled_substance_schedule} Controlled Substance
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Directions */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-400" />
          Directions for Use
        </h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-900 font-medium">
            {prescription.medication_prescribed?.sig?.sig_text || 'No directions provided'}
          </p>
          {prescription.medication_prescribed?.sig?.sig_free_text && (
            <p className="text-blue-800 mt-2 text-sm">
              Additional instructions: {prescription.medication_prescribed.sig.sig_free_text}
            </p>
          )}
        </div>
        
        {prescription.medication_prescribed?.notes && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> {prescription.medication_prescribed.notes}
            </p>
          </div>
        )}
      </div>

      {/* Prescription Details */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Prescription Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {prescription.medication_prescribed?.quantity && (
            <div>
              <p className="text-sm text-gray-500">Quantity</p>
              <p className="font-medium flex items-center gap-2 mt-1">
                <Package className="h-4 w-4 text-gray-400" />
                {prescription.medication_prescribed.quantity.value} {prescription.medication_prescribed.quantity.unit}
              </p>
            </div>
          )}
          
          {prescription.medication_prescribed?.days_supply !== undefined && (
            <div>
              <p className="text-sm text-gray-500">Days Supply</p>
              <p className="font-medium flex items-center gap-2 mt-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                {prescription.medication_prescribed.days_supply} days
              </p>
            </div>
          )}
          
          {prescription.medication_prescribed?.number_of_refills !== undefined && (
            <div>
              <p className="text-sm text-gray-500">Refills</p>
              <p className="font-medium flex items-center gap-2 mt-1">
                <RefreshCw className="h-4 w-4 text-gray-400" />
                {prescription.medication_prescribed.number_of_refills} authorized
              </p>
            </div>
          )}
          
          {prescription.medication_prescribed?.substitutions && (
            <div>
              <p className="text-sm text-gray-500">Generic Substitution</p>
              <p className="font-medium mt-1">
                {prescription.medication_prescribed.substitutions === 0 ? 'Allowed' : 'Not Allowed'}
              </p>
            </div>
          )}
          
          {prescription.medication_prescribed?.ndc && (
            <div>
              <p className="text-sm text-gray-500">NDC</p>
              <p className="font-medium flex items-center gap-2 mt-1">
                <Hash className="h-4 w-4 text-gray-400" />
                {prescription.medication_prescribed.ndc}
              </p>
            </div>
          )}
          
          {prescription.prescribed_date && (
            <div>
              <p className="text-sm text-gray-500">Prescribed Date</p>
              <p className="font-medium mt-1">
                {format(new Date(prescription.prescribed_date), 'MMM d, yyyy')}
              </p>
            </div>
          )}
          
          {prescription.sent_date && (
            <div>
              <p className="text-sm text-gray-500">Sent to Pharmacy</p>
              <p className="font-medium mt-1">
                {format(new Date(prescription.sent_date), 'MMM d, yyyy')}
              </p>
            </div>
          )}
          
          {prescription.last_filled_date && (
            <div>
              <p className="text-sm text-gray-500">Last Filled</p>
              <p className="font-medium mt-1">
                {format(new Date(prescription.last_filled_date), 'MMM d, yyyy')}
              </p>
            </div>
          )}
          
          {prescription.expiration_date && (
            <div>
              <p className="text-sm text-gray-500">Prescription Expires</p>
              <p className="font-medium mt-1 flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                {format(new Date(prescription.expiration_date), 'MMM d, yyyy')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pharmacy Information */}
      {prescription.pharmacy && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-gray-400" />
            Pharmacy
          </h3>
          <div className="space-y-3">
            <p className="font-medium text-gray-900">{prescription.pharmacy.business_name}</p>
            
            {prescription.pharmacy.address && (
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                <div>
                  <p>{prescription.pharmacy.address.line1}</p>
                  {prescription.pharmacy.address.line2 && <p>{prescription.pharmacy.address.line2}</p>}
                  <p>{prescription.pharmacy.address.city}, {prescription.pharmacy.address.state} {prescription.pharmacy.address.zip}</p>
                </div>
              </div>
            )}
            
            {prescription.pharmacy.communication_numbers?.primary_phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="h-4 w-4 text-gray-400" />
                <p>{prescription.pharmacy.communication_numbers.primary_phone}</p>
              </div>
            )}
            
            {prescription.pharmacy.ncpdp_id && (
              <p className="text-xs text-gray-500">NCPDP: {prescription.pharmacy.ncpdp_id}</p>
            )}
          </div>
        </div>
      )}

      {/* Prescriber Information */}
      {prescription.prescriber && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-gray-400" />
            Prescriber
          </h3>
          <div className="space-y-2">
            <p className="font-medium text-gray-900">
              Dr. {prescription.prescriber.name.first_name} {prescription.prescriber.name.last_name}
            </p>
            {prescription.prescriber.communication_numbers?.primary_phone?.number && (
              <p className="text-sm text-gray-600">Phone: {prescription.prescriber.communication_numbers.primary_phone.number}</p>
            )}
          </div>
        </div>
      )}

      {/* Important Information */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-2">Important Information</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Take this medication exactly as prescribed by your doctor</li>
              <li>Do not share this medication with others</li>
              <li>Store in a safe place, away from children</li>
              <li>Contact your doctor if you experience any side effects</li>
              {prescription.controlled_substance_schedule && (
                <li className="font-medium">This is a controlled substance - handle with care</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}